import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ProductSize } from '@/network/graphql.g'

type UseSizeUrlSyncOptions = {
  sizes: ProductSize[]
  initialSelectedSizeId?: string
  onDidSelectSize: (sizeId: string) => void
}

type UseSizeUrlSyncResult = {
  selectedSizeId: string
  selectSize: (sizeId: string) => void
}

export const useSizeUrlSync = ({
  sizes,
  initialSelectedSizeId,
  onDidSelectSize
}: UseSizeUrlSyncOptions): UseSizeUrlSyncResult => {
  const router = useRouter()
  const [selectedSizeId, setSelectedSizeId] = useState<string>(
    initialSelectedSizeId
  )
  const isUserInteractionRef = useRef(false)

  useEffect(() => {
    if (initialSelectedSizeId) {
      setSelectedSizeId(initialSelectedSizeId)
    }
  }, [initialSelectedSizeId])

  useEffect(() => {
    const sizeFromQueryRaw = router.query.size
    const sizeFromQuery = Array.isArray(sizeFromQueryRaw)
      ? sizeFromQueryRaw[0]
      : sizeFromQueryRaw

    const isSizeFromQueryValid =
      !!sizeFromQuery && sizes.some((size) => size.id === sizeFromQuery)

    if (isSizeFromQueryValid && sizeFromQuery !== selectedSizeId) {
      setSelectedSizeId(sizeFromQuery)
      return
    }

    if (
      !isSizeFromQueryValid &&
      initialSelectedSizeId &&
      initialSelectedSizeId !== selectedSizeId
    ) {
      setSelectedSizeId(initialSelectedSizeId)
    }
  }, [router.query.size, initialSelectedSizeId, sizes])

  useEffect(() => {
    if (!selectedSizeId) {
      return
    }

    onDidSelectSize(selectedSizeId)

    if (!isUserInteractionRef.current) {
      return
    }

    const sizeInUrlRaw = router.query.size
    const sizeInUrl = Array.isArray(sizeInUrlRaw)
      ? sizeInUrlRaw[0]
      : sizeInUrlRaw

    if (sizeInUrl !== selectedSizeId) {
      const nextQuery = { ...router.query, size: selectedSizeId }
      router.replace(
        { pathname: router.pathname, query: nextQuery },
        undefined,
        {
          shallow: true
        }
      )
    }

    isUserInteractionRef.current = false
  }, [selectedSizeId])

  const selectSize = (sizeId: string) => {
    isUserInteractionRef.current = true
    setSelectedSizeId(sizeId)
    onDidSelectSize(sizeId)
  }

  return { selectedSizeId, selectSize }
}
